import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Play, Copy, Pencil, Trash2, Check, Disc3 } from "lucide-react";
import { AppShell } from "@/components/AppShell";
import { JamTakeService } from "@/services/JamTakeService";
import { JamTake } from "@/models/types";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n/I18nContext";

export default function MyJams() {
  const navigate = useNavigate();
  const { setResumeTake } = useApp();
  const { t, tm } = useI18n();
  const [takes, setTakes] = useState<JamTake[]>(() => JamTakeService.list());
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const refresh = () => setTakes(JamTakeService.list());

  const resume = (take: JamTake) => {
    setResumeTake(take);
    navigate(`/jam/${take.songId}`);
  };

  const startRename = (take: JamTake) => {
    setEditing(take.id);
    setDraft(take.versionName);
  };
  const commitRename = (id: string) => {
    JamTakeService.rename(id, draft.trim() || t("myjams.untitled"));
    setEditing(null);
    refresh();
    toast.success(t("myjams.toastRenamed"));
  };
  const duplicate = (id: string) => {
    JamTakeService.duplicate(id);
    refresh();
    toast.success(t("myjams.toastDuplicated"));
  };
  const remove = (id: string) => {
    JamTakeService.remove(id);
    refresh();
    toast(t("myjams.toastDeleted"));
  };

  return (
    <AppShell active="my-jams">
      <span className="font-mono text-[11px] uppercase tracking-[0.4em] text-amber-400/90">
        {t("myjams.kicker")}
      </span>
      <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-100">{t("myjams.title")}</h1>
      <p className="mt-2 text-sm text-slate-400">{t("myjams.desc")}</p>

      {takes.length === 0 ? (
        <div
          className="mt-10 flex flex-col items-center justify-center gap-3 rounded-3xl py-16 text-center"
          style={{ background: "rgba(18,21,25,0.5)", border: "1px dashed rgba(255,255,255,0.1)" }}
        >
          <Disc3 size={30} className="text-amber-400/70" />
          <div className="text-sm text-slate-400">{t("myjams.empty")}</div>
          <button
            onClick={() => navigate("/library")}
            className="mt-1 rounded-full px-5 py-2.5 text-xs font-semibold text-[#1a1204]"
            style={{ background: "linear-gradient(135deg,#FDE68A,#F59E0B)" }}
          >
            {t("myjams.startJam")}
          </button>
        </div>
      ) : (
        <div className="mt-7 space-y-3">
          {takes.map((take, i) => (
            <motion.div
              key={take.id}
              data-testid={`jam-take-${take.id}`}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              className="flex flex-col gap-3 rounded-2xl p-4 sm:flex-row sm:items-center sm:justify-between"
              style={{ background: "rgba(18,21,25,0.8)", border: "1px solid rgba(255,255,255,0.08)" }}
            >
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-100">{take.songTitle}</div>
                {editing === take.id ? (
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      autoFocus
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && commitRename(take.id)}
                      className="rounded-md bg-transparent px-2 py-1 text-xs text-slate-200 outline-none"
                      style={{ border: "1px solid rgba(245,158,11,0.4)" }}
                    />
                    <button onClick={() => commitRename(take.id)} className="text-amber-300">
                      <Check size={15} />
                    </button>
                  </div>
                ) : (
                  <div className="text-xs text-slate-400">{take.versionName}</div>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <Tag>{tm("modes", take.mode)}</Tag>
                  <Tag>{tm("progress", take.progress)}</Tag>
                  {take.variation > 0 && <Tag>{t("myjams.variation", { n: take.variation })}</Tag>}
                  <span className="font-mono text-[10px] text-slate-500">
                    {new Date(take.dateISO).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1.5">
                <IconBtn testId={`resume-${take.id}`} onClick={() => resume(take)} title={t("myjams.resume")} primary>
                  <Play size={15} fill="#1a1204" />
                </IconBtn>
                <IconBtn testId={`rename-${take.id}`} onClick={() => startRename(take)} title={t("myjams.rename")}>
                  <Pencil size={15} />
                </IconBtn>
                <IconBtn testId={`duplicate-${take.id}`} onClick={() => duplicate(take.id)} title={t("myjams.duplicate")}>
                  <Copy size={15} />
                </IconBtn>
                <IconBtn testId={`delete-${take.id}`} onClick={() => remove(take.id)} title={t("myjams.delete")}>
                  <Trash2 size={15} />
                </IconBtn>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </AppShell>
  );
}

const Tag: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span
    className="rounded-full px-2 py-0.5 font-mono text-[9px] uppercase tracking-widest text-amber-200"
    style={{ background: "rgba(245,158,11,0.1)", border: "1px solid rgba(245,158,11,0.2)" }}
  >
    {children}
  </span>
);

const IconBtn: React.FC<{
  testId: string;
  onClick: () => void;
  title: string;
  primary?: boolean;
  children: React.ReactNode;
}> = ({ testId, onClick, title, primary, children }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    title={title}
    className={`flex h-9 w-9 items-center justify-center rounded-full transition active:scale-90 ${
      primary ? "text-[#1a1204]" : "text-slate-300 hover:text-amber-200"
    }`}
    style={
      primary
        ? { background: "linear-gradient(135deg,#FDE68A,#F59E0B)" }
        : { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }
    }
  >
    {children}
  </button>
);
